import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import config from 'config';
import { connect } from 'react-redux';
import { ActionTypes } from 'constants/index';
import {
  getLocationImports,
  getLocationImport,
  updateLocationImportRow,
  approveLocationImport,
  deleteLocationImport,
  getAdminsLocations,
  getBrandsForFilter,
  getAccountsForFilter,
  getStates,
  getCitiesByState,
  getWaterFilterConfigurationsForFilter,
  getReverseOsmosisConfigurationsForFilter,
  getSoftenerConfigurationsForFilter,
} from 'actions/index';
import { Colors } from 'modules/theme';
import Loader from 'components/Loader';

/* ─────────────────────────────────────────────────────────────
   Design tokens (Linear/Vercel-inspired)
───────────────────────────────────────────────────────────── */
const tokens = {
  surface: '#ffffff',
  surfaceHover: '#fafafa',
  surfaceActive: '#f4f4f5',
  border: '#e4e4e7',
  borderSubtle: '#f0f0f2',
  text: '#18181b',
  textMuted: '#71717a',
  textPlaceholder: '#a1a1aa',
  accent: Colors.lightBlue,
  accentHover: '#1282b3',
  success: '#16a34a',
  successBg: '#dcfce7',
  warning: '#ea580c',
  warningBg: '#ffedd5',
  error: '#dc2626',
  errorBg: '#fee2e2',
  infoBg: '#e0f2fe',
  radius: '6px',
  radiusLg: '10px',
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
  shadowMd: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
};

/* ─────────────────────────────────────────────────────────────
   Page Shell
───────────────────────────────────────────────────────────── */
const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  background: #fafafa;
  overflow: hidden;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 24px;
  background: ${tokens.surface};
  border-bottom: 1px solid ${tokens.border};
  flex-shrink: 0;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-width: 0;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: ${tokens.text};
  margin: 0;
  white-space: nowrap;
`;

/* ─────────────────────────────────────────────────────────────
   Split Pane Layout
───────────────────────────────────────────────────────────── */
const SplitPane = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Pane = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${props => (props.$alt ? '#fafafa' : tokens.surface)};
  border-right: ${props => (props.$noBorder ? 'none' : `1px solid ${tokens.border}`)};
`;

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid ${tokens.borderSubtle};
  flex-shrink: 0;
`;

const PaneTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: ${tokens.text};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PaneBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => (props.$noPadding ? '0' : '12px 16px')};
`;

/* ─────────────────────────────────────────────────────────────
   Buttons
───────────────────────────────────────────────────────────── */
const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  border-radius: ${tokens.radius};
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnPrimary = styled(Btn)`
  background: ${tokens.accent};
  color: #fff;
  border-color: ${tokens.accent};

  &:hover:not(:disabled) {
    background: ${tokens.accentHover};
    border-color: ${tokens.accentHover};
  }
`;

const BtnSecondary = styled(Btn)`
  background: ${tokens.surface};
  color: ${tokens.text};
  border-color: ${tokens.border};

  &:hover:not(:disabled) {
    background: ${tokens.surfaceHover};
  }
`;

const BtnGhost = styled(Btn)`
  background: transparent;
  color: ${tokens.textMuted};
  border-color: transparent;
  padding: 6px 10px;

  &:hover:not(:disabled) {
    background: ${tokens.surfaceActive};
    color: ${tokens.text};
  }
`;

/* ─────────────────────────────────────────────────────────────
   Form Elements
───────────────────────────────────────────────────────────── */
const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 500;
  color: ${tokens.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 0 10px;
  font-size: 13px;
  color: ${tokens.text};
  background: ${tokens.surface};
  border: 1px solid ${tokens.border};
  border-radius: ${tokens.radius};
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${tokens.textPlaceholder};
  }

  &:focus {
    border-color: ${tokens.accent};
    box-shadow: 0 0 0 3px rgba(20, 152, 203, 0.1);
  }
`;

const SelectEl = styled.select`
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 0 10px;
  font-size: 13px;
  color: ${tokens.text};
  background: ${tokens.surface};
  border: 1px solid ${tokens.border};
  border-radius: ${tokens.radius};
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;

  &:focus {
    border-color: ${tokens.accent};
    box-shadow: 0 0 0 3px rgba(20, 152, 203, 0.1);
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${tokens.accent};
`;

const SearchInput = styled(Input)`
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23a1a1aa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'%3E%3C/circle%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'%3E%3C/line%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px center;
  padding-left: 32px;
`;

/* ─────────────────────────────────────────────────────────────
   Badges / Pills
───────────────────────────────────────────────────────────── */
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 999px;
  text-transform: capitalize;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `background: ${tokens.successBg}; color: ${tokens.success};`;
      case 'warning':
        return `background: ${tokens.warningBg}; color: ${tokens.warning};`;
      case 'error':
        return `background: ${tokens.errorBg}; color: ${tokens.error};`;
      case 'info':
        return `background: ${tokens.infoBg}; color: ${tokens.accent};`;
      default:
        return `background: ${tokens.surfaceActive}; color: ${tokens.textMuted};`;
    }
  }}
`;

const StatBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  background: ${tokens.surfaceActive};
  color: ${tokens.textMuted};
  border-radius: ${tokens.radius};
`;

/* ─────────────────────────────────────────────────────────────
   Row List
───────────────────────────────────────────────────────────── */
const RowListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${tokens.borderSubtle};
  background: ${tokens.surface};
  flex-shrink: 0;
`;

const RowListHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const RowListHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ImportSelect = styled.select`
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  color: ${tokens.text};
  background: ${tokens.surface};
  border: 1px solid ${tokens.border};
  border-radius: ${tokens.radius};
  outline: none;
  cursor: pointer;
  max-width: 200px;

  &:focus {
    border-color: ${tokens.accent};
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 16px;
  color: ${tokens.textMuted};
  background: ${tokens.surface};
  border: 1px solid ${tokens.border};
  border-radius: ${tokens.radius};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${tokens.surfaceHover};
    color: ${tokens.text};
    border-color: ${tokens.accent};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FilterTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: ${tokens.surfaceActive};
  border-radius: ${tokens.radius};
`;

const FilterTab = styled.button`
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => (props.$active ? tokens.text : tokens.textMuted)};
  background: ${props => (props.$active ? tokens.surface : 'transparent')};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.1s ease;
  box-shadow: ${props => (props.$active ? tokens.shadow : 'none')};

  &:hover {
    color: ${tokens.text};
  }
`;

const RowItem = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${tokens.borderSubtle};
  background: ${props => (props.$active ? tokens.surfaceActive : 'transparent')};
  transition: background 0.1s ease;

  &:hover {
    background: ${props => (props.$active ? tokens.surfaceActive : tokens.surfaceHover)};
  }
`;

const RowItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RowItemTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${tokens.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowItemSub = styled.div`
  font-size: 11px;
  color: ${tokens.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IssueIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 50%;
  background: ${props => (props.$error ? tokens.errorBg : tokens.warningBg)};
  color: ${props => (props.$error ? tokens.error : tokens.warning)};
`;

/* ─────────────────────────────────────────────────────────────
   Inspector
───────────────────────────────────────────────────────────── */
const InspectorEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
  color: ${tokens.textMuted};
`;

const InspectorEmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const InspectorEmptyText = styled.div`
  font-size: 14px;
`;

const InspectorSection = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${tokens.borderSubtle};

  &:last-child {
    border-bottom: none;
  }
`;

const InspectorSectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 600;
  color: ${tokens.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const DeviceConfigLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const DeviceConfigColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DeviceConfigColumnTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${tokens.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const DeviceConfigHint = styled.div`
  font-size: 12px;
  color: ${tokens.textMuted};
  background: ${tokens.surfaceHover};
  border: 1px dashed ${tokens.border};
  border-radius: ${tokens.radius};
  padding: 10px 12px;
`;

const IssuesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IssueItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  font-size: 12px;
  border-radius: ${tokens.radius};
  background: ${props => (props.$error ? tokens.errorBg : tokens.warningBg)};
  color: ${props => (props.$error ? tokens.error : tokens.warning)};
`;

const IssueIcon = styled.span`
  flex-shrink: 0;
  font-size: 14px;
`;

/* ─────────────────────────────────────────────────────────────
   Modal / Dialog
───────────────────────────────────────────────────────────── */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: ${tokens.surface};
  border-radius: ${tokens.radiusLg};
  box-shadow: ${tokens.shadowMd};
  width: 100%;
  max-width: 420px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${tokens.border};
`;

const ModalTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${tokens.text};
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 20px;
  font-size: 14px;
  color: ${tokens.textMuted};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid ${tokens.border};
  background: ${tokens.surfaceHover};
`;

/* ─────────────────────────────────────────────────────────────
   Result Banner
───────────────────────────────────────────────────────────── */
const ResultBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  font-size: 13px;
  background: ${props => (props.$hasErrors ? tokens.warningBg : tokens.successBg)};
  color: ${props => (props.$hasErrors ? tokens.warning : tokens.success)};
  border-bottom: 1px solid ${tokens.borderSubtle};
`;

const ResultBannerClose = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

/* ─────────────────────────────────────────────────────────────
   Misc
───────────────────────────────────────────────────────────── */
const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${tokens.infoBg};
  border-bottom: 1px solid ${tokens.borderSubtle};
  font-size: 13px;
  color: ${tokens.accent};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 40px;
  color: ${tokens.border};
  margin-bottom: 16px;
`;

const EmptyStateText = styled.div`
  font-size: 14px;
  color: ${tokens.textMuted};
`;

const InspectorActions = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px;
  border-top: 1px solid ${tokens.border};
  background: ${tokens.surfaceHover};
`;

/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
const getNested = (obj, path, fallback = '') => {
  const parts = path.split('.');
  let cursor = obj;
  for (let i = 0; i < parts.length; i += 1) {
    if (!cursor || cursor[parts[i]] === undefined || cursor[parts[i]] === null) {
      return fallback;
    }
    cursor = cursor[parts[i]];
  }
  return cursor;
};

const getStatusVariant = status => {
  if (status === 'approved') return 'success';
  if (status === 'ready') return 'info';
  if (status === 'error') return 'error';
  return 'warning';
};

const getStatusLabel = status => {
  if (status === 'needs_data') return 'Needs Data';
  return status;
};

const normalizeLabel = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const getBrandIdFromPayload = (payload, brands = []) => {
  if (!payload) return '';

  const explicitBrandId = payload.brand_id ? parseInt(payload.brand_id, 10) : null;
  if (explicitBrandId) return explicitBrandId;

  if (!payload.brand_name) return '';

  const targetLabel = normalizeLabel(payload.brand_name);
  const matchedBrand = brands.find(brand => normalizeLabel(brand.label) === targetLabel);

  return matchedBrand ? matchedBrand.value : '';
};

const sortFilterOptions = (options = []) =>
  [...options].sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));

const DEVICE_ORDER = ['waterFilter', 'reverseOsmosis', 'waterSoftener'];

const DEVICE_META = {
  waterFilter: {
    configPath: 'waterFilter.water_filter_config_id',
    locationIdPath: 'waterFilter.location_identificator',
  },
  reverseOsmosis: {
    configPath: 'reverseOsmosis.ro_filter_config_id',
    locationIdPath: 'reverseOsmosis.location_identificator',
  },
  waterSoftener: {
    configPath: 'waterSoftener.softener_configuration_id',
    locationIdPath: 'waterSoftener.location_identificator',
  },
};

const clonePayload = payload => JSON.parse(JSON.stringify(payload || {}));

const setNested = (obj, path, value) => {
  const parts = path.split('.');
  let cursor = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (!cursor[parts[i]] || typeof cursor[parts[i]] !== 'object') {
      cursor[parts[i]] = {};
    }
    cursor = cursor[parts[i]];
  }
  cursor[parts[parts.length - 1]] = value;
};

const stripDeviceConfigSuffix = value =>
  String(value || '')
    .replace(/\s*device\s*config\s*$/i, '')
    .trim();

const normalizeStreetAddress = value => {
  const cleaned = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!cleaned) return '';

  let street = cleaned.split(',')[0].trim();
  street = street.replace(
    /\s+\b(?:apt|apartment|suite|ste|unit|floor|fl|building|bldg)\b\.?\s*.*$/i,
    '',
  );
  street = street.replace(/\s+#\s*\w.*$/i, '').trim();
  return street;
};

const buildDeviceLocationId = (deviceName, addressLine) => {
  const normalizedDeviceName = stripDeviceConfigSuffix(deviceName);
  const normalizedAddress = normalizeStreetAddress(addressLine);

  if (!normalizedDeviceName && !normalizedAddress) return '';
  if (!normalizedDeviceName) return normalizedAddress;
  if (!normalizedAddress) return normalizedDeviceName;
  return `${normalizedDeviceName} / ${normalizedAddress}`;
};

const hasFieldValue = value => value !== null && value !== undefined && String(value).trim() !== '';

const parseBooleanLike = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (!normalized) return fallback;
  if (['true', 'yes', 'y', 'on'].includes(normalized)) return true;
  if (['false', 'no', 'n', 'off'].includes(normalized)) return false;
  return fallback;
};

const hasAdminDetails = payload =>
  [
    'admin_first_name',
    'admin_last_name',
    'admin_email',
    'admin_phone_number',
    'admin_position',
  ].some(fieldName => hasFieldValue(payload[fieldName]));

const hasContactDetails = payload =>
  [
    'contact_first_name',
    'contact_last_name',
    'contact_email',
    'contact_phone_number',
    'contact_position',
  ].some(fieldName => hasFieldValue(payload[fieldName]));

const deriveAdminSelectionValue = payload => {
  if (!payload) return 'add_later';
  const locationAdminId = parseInt(payload.location_admin_id, 10);
  if (locationAdminId) return String(locationAdminId);
  if (parseBooleanLike(payload.createNewUser, false)) return 'new_user';
  return 'add_later';
};

const STATE_ABBR_TO_NAME = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

const getZipForLookup = zipCode => {
  const s = String(zipCode || '').trim();
  if (!s) return null;
  const digits = s.replace(/\D/g, '');
  return digits.length >= 5 ? digits.substring(0, 5) : null;
};

const hydrateAdminAndContactPayload = payload => {
  const hydrated = clonePayload(payload || {});
  const locationAdminId = parseInt(hydrated.location_admin_id, 10);
  const resolvedLocationAdminId = Number.isNaN(locationAdminId) ? '' : locationAdminId;
  const inferredCreateNewUser = resolvedLocationAdminId ? false : hasAdminDetails(hydrated);
  const inferredIsPointOfContact = !hasContactDetails(hydrated);

  hydrated.location_admin_id = resolvedLocationAdminId;
  hydrated.createNewUser = resolvedLocationAdminId
    ? false
    : parseBooleanLike(hydrated.createNewUser, inferredCreateNewUser);
  hydrated.isPointOfContact = parseBooleanLike(hydrated.isPointOfContact, inferredIsPointOfContact);

  if (hydrated.createNewUser) {
    hydrated.location_admin_id = '';
  }

  if (!hydrated.createNewUser && !hydrated.location_admin_id) {
    hydrated.isPointOfContact = false;
  }

  return hydrated;
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
class LocationImportApprovalContainer extends Component {
  state = {
    selectedImportId: null,
    selectedRowId: null,
    selectedRowIds: {},
    editorPayload: null,
    originalPayload: null,
    statusFilter: 'all',
    searchQuery: '',
    confirmModal: null,
    showResultBanner: false,
    pendingAdminLookupAccountId: null,
    pendingZipResolution: null,
  };

  fileInputRef = React.createRef();

  getConfigOptionsByType = () => {
    const { wfConfigs, roConfigs, softenerConfigs } = this.props;
    return {
      waterFilter: wfConfigs || [],
      reverseOsmosis: roConfigs || [],
      waterSoftener: softenerConfigs || [],
    };
  };

  getGeneratedDeviceLocationId = (
    payload,
    deviceType,
    configOptionsByType = this.getConfigOptionsByType(),
  ) => {
    if (!payload || !DEVICE_META[deviceType]) return '';

    const { configPath } = DEVICE_META[deviceType];
    const selectedConfigId = parseInt(getNested(payload, configPath, ''), 10);
    if (!selectedConfigId) return '';

    const matchedConfig = (configOptionsByType[deviceType] || []).find(
      option => parseInt(option.value, 10) === selectedConfigId,
    );

    return buildDeviceLocationId(matchedConfig ? matchedConfig.label : '', payload.address_line);
  };

  resolveFromZipCode = zipCode => {
    const zip = getZipForLookup(zipCode);
    if (!zip || zip.length !== 5) return;

    const { states, fetchCities } = this.props;
    fetch(`https://api.zippopotam.us/us/${zip}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!data?.places?.length) return;
        const stateAbbr = data.places[0]['state abbreviation'];
        const stateName = STATE_ABBR_TO_NAME[stateAbbr] || '';
        const cityName = data.places[0]['place name'];

        const stateObj = states.find(s => {
          if (s.label?.includes(stateAbbr)) return true;
          if (stateName && s.label?.includes(stateName)) return true;
          return false;
        });
        if (!stateObj) return;

        this.setState(prev => {
          const next = clonePayload(prev.editorPayload || {});
          next.state_id = stateObj.value;
          next.city_id = '';
          return {
            editorPayload: next,
            pendingZipResolution: { stateId: stateObj.value, cityName },
          };
        });
        fetchCities(stateObj.value);
      })
      .catch(() => {});
  };

  getPrimaryDeviceType = payload => {
    if (!payload) return null;
    return DEVICE_ORDER.find(deviceType => {
      const { configPath } = DEVICE_META[deviceType];
      return !!parseInt(getNested(payload, configPath, ''), 10);
    });
  };

  applyDeviceLocationIdRules = (nextPayload, previousPayload = null, changedPath = null) => {
    if (!nextPayload) return nextPayload;

    const configOptionsByType = this.getConfigOptionsByType();

    DEVICE_ORDER.forEach(deviceType => {
      const { configPath, locationIdPath } = DEVICE_META[deviceType];
      const nextConfigId = parseInt(getNested(nextPayload, configPath, ''), 10);

      if (!nextConfigId) {
        setNested(nextPayload, locationIdPath, '');
        return;
      }

      if (changedPath === locationIdPath) {
        return;
      }

      const currentLocationId = String(getNested(nextPayload, locationIdPath, '') || '').trim();
      const previousLocationId = String(
        getNested(previousPayload || {}, locationIdPath, '') || '',
      ).trim();
      const previousGeneratedId = this.getGeneratedDeviceLocationId(
        previousPayload || {},
        deviceType,
        configOptionsByType,
      );
      const nextGeneratedId = this.getGeneratedDeviceLocationId(
        nextPayload,
        deviceType,
        configOptionsByType,
      );

      const shouldAutofill =
        !currentLocationId ||
        (!!previousGeneratedId &&
          (currentLocationId === previousGeneratedId ||
            previousLocationId === previousGeneratedId));

      if (shouldAutofill && nextGeneratedId) {
        setNested(nextPayload, locationIdPath, nextGeneratedId);
      }
    });

    if (changedPath !== 'identificator') {
      const previousPrimaryType = this.getPrimaryDeviceType(previousPayload || {});
      const nextPrimaryType = this.getPrimaryDeviceType(nextPayload);

      if (nextPrimaryType) {
        const { locationIdPath } = DEVICE_META[nextPrimaryType];
        const nextPrimaryLocationId = String(
          getNested(nextPayload, locationIdPath, '') || '',
        ).trim();
        const previousPrimaryLocationId = previousPrimaryType
          ? String(
              getNested(
                previousPayload || {},
                DEVICE_META[previousPrimaryType].locationIdPath,
                '',
              ) || '',
            ).trim()
          : '';
        const currentIdentificator = String(nextPayload.identificator || '').trim();

        if (
          !currentIdentificator ||
          currentIdentificator === previousPrimaryLocationId ||
          previousPrimaryType !== nextPrimaryType
        ) {
          nextPayload.identificator = nextPrimaryLocationId || '';
        }
      } else if (!nextPayload.identificator) {
        nextPayload.identificator = '';
      }
    }

    return nextPayload;
  };

  applyAdminSelection = selectionValue => {
    this.setState(prevState => {
      const nextPayload = clonePayload(prevState.editorPayload || {});

      if (selectionValue === 'new_user') {
        nextPayload.createNewUser = true;
        nextPayload.location_admin_id = '';
      } else if (selectionValue === 'add_later' || !selectionValue) {
        nextPayload.createNewUser = false;
        nextPayload.location_admin_id = '';
        nextPayload.isPointOfContact = false;
      } else {
        const parsedAdminId = parseInt(selectionValue, 10);
        nextPayload.createNewUser = false;
        nextPayload.location_admin_id = Number.isNaN(parsedAdminId) ? '' : parsedAdminId;
        nextPayload.isPointOfContact = !Number.isNaN(parsedAdminId);
      }

      return {
        editorPayload: hydrateAdminAndContactPayload(nextPayload),
      };
    });
  };

  componentDidMount() {
    const {
      fetchImports,
      fetchBrands,
      fetchAccounts,
      fetchStates,
      fetchWfConfigs,
      fetchRoConfigs,
      fetchSoftenerConfigs,
    } = this.props;
    fetchImports();
    fetchBrands();
    fetchAccounts({});
    fetchStates();
    fetchWfConfigs();
    fetchRoConfigs();
    fetchSoftenerConfigs();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      imports,
      rows,
      brands,
      cities,
      adminsLocations,
      wfConfigs,
      roConfigs,
      softenerConfigs,
      lastApproveResult,
    } = this.props;
    const {
      selectedImportId,
      selectedRowId,
      editorPayload,
      pendingAdminLookupAccountId,
    } = this.state;

    // Auto-select first import
    if (!selectedImportId && imports.length > 0 && prevProps.imports !== imports) {
      this.selectImport(imports[0].id);
      return;
    }

    // Fetch import details when selection changes
    if (selectedImportId !== prevState.selectedImportId && selectedImportId) {
      this.props.fetchImport(selectedImportId);
      return;
    }

    // Auto-select first row
    if (rows !== prevProps.rows && rows.length > 0 && !selectedRowId) {
      this.selectRow(rows[0].id);
      return;
    }

    // Update editor when row data changes from backend
    if (rows !== prevProps.rows && selectedRowId) {
      const currentRow = rows.find(row => row.id === selectedRowId);
      const prevRow = prevProps.rows.find(row => row.id === selectedRowId);
      const currentPayload = currentRow
        ? JSON.stringify(currentRow.normalized_payload || {})
        : null;
      const prevPayload = prevRow ? JSON.stringify(prevRow.normalized_payload || {}) : null;
      if (currentPayload && currentPayload !== prevPayload) {
        const parsed = this.applyDeviceLocationIdRules(JSON.parse(currentPayload));
        const hydratedPayload = hydrateAdminAndContactPayload(parsed);
        this.setState({
          editorPayload: hydratedPayload,
          originalPayload: clonePayload(hydratedPayload),
        });
      }
    }

    if (brands !== prevProps.brands && selectedRowId && editorPayload) {
      const derivedBrandId = getBrandIdFromPayload(editorPayload, brands);
      if (derivedBrandId && derivedBrandId !== editorPayload.brand_id) {
        this.setState(prev => ({
          editorPayload: {
            ...prev.editorPayload,
            brand_id: derivedBrandId,
          },
        }));
        this.props.fetchAccounts({ brands: [derivedBrandId] });
      }
    }

    if (
      selectedRowId &&
      editorPayload &&
      (wfConfigs !== prevProps.wfConfigs ||
        roConfigs !== prevProps.roConfigs ||
        softenerConfigs !== prevProps.softenerConfigs)
    ) {
      const nextPayload = clonePayload(editorPayload);
      this.applyDeviceLocationIdRules(nextPayload, editorPayload);
      const hydratedPayload = hydrateAdminAndContactPayload(nextPayload);
      if (JSON.stringify(hydratedPayload) !== JSON.stringify(editorPayload)) {
        this.setState({ editorPayload: hydratedPayload });
      }
    }

    if (cities !== prevProps.cities && this.state.pendingZipResolution && editorPayload) {
      const { stateId, cityName } = this.state.pendingZipResolution;
      if (editorPayload.state_id === stateId && cityName) {
        const cityObj = (cities || []).find(
          c => c.label?.toLowerCase() === cityName?.toLowerCase(),
        );
        if (cityObj) {
          const nextPayload = clonePayload(editorPayload);
          nextPayload.city_id = cityObj.value;
          this.setState({
            editorPayload: nextPayload,
            pendingZipResolution: null,
          });
          return;
        }
      }
      this.setState({ pendingZipResolution: null });
    }

    if (
      adminsLocations !== prevProps.adminsLocations &&
      pendingAdminLookupAccountId &&
      editorPayload
    ) {
      const selectedAdminId = parseInt(editorPayload.location_admin_id, 10);
      const adminIsValid =
        !!selectedAdminId &&
        adminsLocations.some(admin => parseInt(admin.id, 10) === selectedAdminId);

      if (selectedAdminId && !adminIsValid) {
        const nextPayload = clonePayload(editorPayload);
        nextPayload.location_admin_id = '';
        nextPayload.createNewUser = false;
        nextPayload.isPointOfContact = false;
        this.setState({
          editorPayload: hydrateAdminAndContactPayload(nextPayload),
          pendingAdminLookupAccountId: null,
        });
        return;
      }

      this.setState({ pendingAdminLookupAccountId: null });
    }

    // Show result banner when approval completes
    if (lastApproveResult && lastApproveResult !== prevProps.lastApproveResult) {
      this.setState({ showResultBanner: true });
    }
  }

  hasUnsavedChanges = () => {
    const { editorPayload, originalPayload } = this.state;
    if (!editorPayload || !originalPayload) return false;
    return JSON.stringify(editorPayload) !== JSON.stringify(originalPayload);
  };

  confirmIfDirty = callback => {
    if (this.hasUnsavedChanges()) {
      this.setState({
        confirmModal: {
          title: 'Unsaved Changes',
          message: 'You have unsaved changes. Discard them and continue?',
          confirmLabel: 'Discard',
          onConfirm: () => {
            this.setState({ confirmModal: null });
            callback();
          },
        },
      });
    } else {
      callback();
    }
  };

  selectImport = importId => {
    this.confirmIfDirty(() => {
      this.setState({
        selectedImportId: importId,
        selectedRowId: null,
        selectedRowIds: {},
        editorPayload: null,
        originalPayload: null,
        showResultBanner: false,
      });
      this.props.clearApproveResult();
    });
  };

  selectRow = rowId => {
    this.confirmIfDirty(() => {
      const { rows, brands } = this.props;
      const row = rows.find(item => item.id === rowId);
      const payload = row ? JSON.parse(JSON.stringify(row.normalized_payload || {})) : null;

      const derivedBrandId = getBrandIdFromPayload(payload, brands);
      if (payload && derivedBrandId) {
        payload.brand_id = derivedBrandId;
        this.props.fetchAccounts({ brands: [derivedBrandId] });
      } else {
        this.props.fetchAccounts({});
      }

      const hydratedPayload = payload
        ? hydrateAdminAndContactPayload(this.applyDeviceLocationIdRules(payload))
        : null;
      const accountId = hydratedPayload ? parseInt(hydratedPayload.account_id, 10) : null;

      if (accountId) {
        this.props.fetchAdminsLocations(accountId);
      }

      this.setState({
        selectedRowId: rowId,
        editorPayload: hydratedPayload,
        originalPayload: hydratedPayload ? clonePayload(hydratedPayload) : null,
        pendingAdminLookupAccountId: accountId || null,
        pendingZipResolution: null,
      });

      if (row && row.normalized_payload && row.normalized_payload.state_id) {
        this.props.fetchCities(row.normalized_payload.state_id);
      }

      const zip = getZipForLookup(hydratedPayload?.zip_code);
      const needsZipResolution =
        zip && zip.length === 5 && (!hydratedPayload?.state_id || !hydratedPayload?.city_id);
      if (needsZipResolution) {
        this.setState({ pendingZipResolution: null });
        this.resolveFromZipCode(hydratedPayload.zip_code);
      }
    });
  };

  openFilePicker = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  handleFileSelect = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    const { token, fetchImports, fetchImport, receiveUploadedImport } = this.props;

    const formData = new FormData();
    formData.append('csv', file);

    try {
      const response = await fetch(`${config.BASE_URL}/location-imports/upload`, {
        method: 'POST',
        headers: {
          jwt: token,
        },
        body: formData,
      });

      const payload = await response.json();

      if (response.ok && payload) {
        receiveUploadedImport(payload);
        fetchImports();
        if (payload.import && payload.import.id) {
          this.setState({
            selectedImportId: payload.import.id,
            selectedRowId: null,
            selectedRowIds: {},
            editorPayload: null,
            originalPayload: null,
          });
          fetchImport(payload.import.id);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Upload failed:', err);
    }

    // Reset file input so same file can be selected again
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  toggleRowSelection = (rowId, checked, event) => {
    if (event) event.stopPropagation();
    this.setState(prevState => ({
      selectedRowIds: {
        ...prevState.selectedRowIds,
        [rowId]: checked,
      },
    }));
  };

  selectAllVisible = () => {
    const filteredRows = this.getFilteredRows();
    const selected = {};
    filteredRows.forEach(row => {
      if (row.status !== 'approved') selected[row.id] = true;
    });
    this.setState({ selectedRowIds: selected });
  };

  clearSelection = () => {
    this.setState({ selectedRowIds: {} });
  };

  getSelectedCount = () => {
    const { selectedRowIds } = this.state;
    return Object.values(selectedRowIds).filter(Boolean).length;
  };

  updateEditorField = (path, value) => {
    this.setState(prevState => {
      const previousPayload = clonePayload(prevState.editorPayload || {});
      const nextPayload = clonePayload(prevState.editorPayload || {});
      setNested(nextPayload, path, value);
      this.applyDeviceLocationIdRules(nextPayload, previousPayload, path);
      return { editorPayload: nextPayload };
    });
  };

  onStateChange = value => {
    this.updateEditorField('state_id', value ? parseInt(value, 10) : '');
    this.updateEditorField('city_id', '');
    if (value) this.props.fetchCities(value);
  };

  onBrandChange = value => {
    const brandId = value ? parseInt(value, 10) : '';
    this.setState(prevState => {
      const previousPayload = clonePayload(prevState.editorPayload || {});
      const nextPayload = clonePayload(prevState.editorPayload || {});
      nextPayload.brand_id = brandId;
      nextPayload.account_id = '';

      const currentAdminSelection = deriveAdminSelectionValue(nextPayload);
      if (currentAdminSelection !== 'new_user') {
        nextPayload.createNewUser = false;
        nextPayload.location_admin_id = '';
        nextPayload.isPointOfContact = false;
      } else {
        nextPayload.location_admin_id = '';
      }

      this.applyDeviceLocationIdRules(nextPayload, previousPayload, 'brand_id');

      return {
        editorPayload: hydrateAdminAndContactPayload(nextPayload),
        pendingAdminLookupAccountId: null,
      };
    });

    if (brandId) {
      this.props.fetchAccounts({ brands: [brandId] });
      return;
    }
    this.props.fetchAccounts({});
  };

  onAccountChange = value => {
    const accountId = value ? parseInt(value, 10) : '';
    this.setState(prevState => {
      const previousPayload = clonePayload(prevState.editorPayload || {});
      const nextPayload = clonePayload(prevState.editorPayload || {});
      nextPayload.account_id = accountId;

      const currentAdminSelection = deriveAdminSelectionValue(nextPayload);
      if (currentAdminSelection !== 'new_user') {
        nextPayload.createNewUser = false;
        nextPayload.location_admin_id = '';
        nextPayload.isPointOfContact = false;
      } else {
        nextPayload.location_admin_id = '';
      }

      this.applyDeviceLocationIdRules(nextPayload, previousPayload, 'account_id');

      return {
        editorPayload: hydrateAdminAndContactPayload(nextPayload),
        pendingAdminLookupAccountId: accountId || null,
      };
    });

    if (accountId) {
      this.props.fetchAdminsLocations(accountId);
    }
  };

  saveRow = () => {
    const { selectedImportId, selectedRowId, editorPayload } = this.state;
    if (!selectedImportId || !selectedRowId || !editorPayload) return;
    this.props.saveImportRow(selectedImportId, selectedRowId, editorPayload);
    // Update original to reflect saved state
    this.setState({ originalPayload: JSON.parse(JSON.stringify(editorPayload)) });
  };

  approveSelected = () => {
    const { selectedImportId, selectedRowIds } = this.state;
    if (!selectedImportId) return;
    const rowIds = Object.keys(selectedRowIds)
      .filter(rowId => selectedRowIds[rowId])
      .map(rowId => parseInt(rowId, 10));
    if (!rowIds.length) return;

    this.setState({
      confirmModal: {
        title: 'Approve Selected Rows',
        message: `Are you sure you want to approve ${rowIds.length} selected row(s)?`,
        confirmLabel: 'Approve',
        onConfirm: () => {
          this.setState({ confirmModal: null, selectedRowIds: {} });
          this.props.approveImportRows(selectedImportId, { rowIds });
        },
      },
    });
  };

  approveAllReady = () => {
    const { selectedImportId } = this.state;
    const { currentImport } = this.props;
    if (!selectedImportId) return;

    const readyCount = currentImport?.ready_rows || 0;

    this.setState({
      confirmModal: {
        title: 'Approve All Ready Rows',
        message: `Are you sure you want to approve all ${readyCount} ready row(s)?`,
        confirmLabel: 'Approve All',
        onConfirm: () => {
          this.setState({ confirmModal: null, selectedRowIds: {} });
          this.props.approveImportRows(selectedImportId, { all: true });
        },
      },
    });
  };

  closeConfirmModal = () => {
    this.setState({ confirmModal: null });
  };

  closeResultBanner = () => {
    this.setState({ showResultBanner: false });
  };

  getFilteredRows = () => {
    const { rows } = this.props;
    const { statusFilter, searchQuery } = this.state;

    let filtered = rows;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(row => row.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(row => {
        const id = getNested(row, 'normalized_payload.identificator', '').toLowerCase();
        const account = getNested(row, 'normalized_payload.account_name', '').toLowerCase();
        const address = getNested(row, 'normalized_payload.address_line', '').toLowerCase();
        return id.includes(q) || account.includes(q) || address.includes(q);
      });
    }

    return filtered;
  };

  deleteImport = () => {
    const { selectedImportId } = this.state;
    if (!selectedImportId) return;
    const { currentImport } = this.props;
    const label = currentImport?.filename || `Import #${selectedImportId}`;
    this.setState({
      confirmModal: {
        title: 'Delete import',
        message: `Delete "${label}" and all its rows? This cannot be undone.`,
        confirmLabel: 'Delete',
        onConfirm: () => {
          this.setState({
            confirmModal: null,
            selectedImportId: null,
            selectedRowId: null,
            selectedRowIds: {},
            editorPayload: null,
            originalPayload: null,
          });
          this.props.deleteLocationImport(selectedImportId);
        },
      },
    });
  };

  renderRowList = () => {
    const { imports, rowsLoading, uploading, deleting } = this.props;
    const {
      selectedImportId,
      selectedRowId,
      selectedRowIds,
      statusFilter,
      searchQuery,
    } = this.state;

    const filteredRows = this.getFilteredRows();

    return (
      <>
        <RowListHeader>
          <RowListHeaderLeft>
            <ImportSelect
              value={selectedImportId || ''}
              onChange={e => this.selectImport(parseInt(e.target.value, 10))}
            >
              {imports.length === 0 ? (
                <option value="">No imports</option>
              ) : (
                imports.map(imp => (
                  <option key={imp.id} value={imp.id}>
                    {imp.filename || `Import #${imp.id}`}
                  </option>
                ))
              )}
            </ImportSelect>
            <FilterTabs>
              {['all', 'needs_data', 'ready', 'error', 'approved'].map(status => (
                <FilterTab
                  key={status}
                  $active={statusFilter === status}
                  onClick={() => this.setState({ statusFilter: status })}
                >
                  {status === 'all' ? 'All' : getStatusLabel(status)}
                </FilterTab>
              ))}
            </FilterTabs>
          </RowListHeaderLeft>
          <RowListHeaderRight>
            {selectedImportId && (
              <IconBtn onClick={this.deleteImport} disabled={deleting} title="Delete import">
                {deleting ? <Loader size={14} /> : <i className="fas fa-trash" />}
              </IconBtn>
            )}
            <IconBtn onClick={this.openFilePicker} disabled={uploading} title="Upload CSV">
              {uploading ? <Loader size={14} /> : <i className="fas fa-plus" />}
            </IconBtn>
            <HiddenFileInput
              ref={this.fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={this.handleFileSelect}
            />
          </RowListHeaderRight>
        </RowListHeader>

        <div style={{ padding: '8px 16px', borderBottom: `1px solid ${tokens.borderSubtle}` }}>
          <SearchInput
            placeholder="Search location, account..."
            value={searchQuery}
            onChange={e => this.setState({ searchQuery: e.target.value })}
          />
        </div>

        {this.getSelectedCount() > 0 && (
          <SelectionBar>
            <span>
              <strong>{this.getSelectedCount()}</strong> selected
            </span>
            <BtnGhost onClick={this.selectAllVisible}>Select all visible</BtnGhost>
            <BtnGhost onClick={this.clearSelection}>Clear</BtnGhost>
          </SelectionBar>
        )}

        <PaneBody $noPadding>
          {rowsLoading ? (
            <EmptyState>
              <Loader size={40} />
            </EmptyState>
          ) : filteredRows.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <i className="fas fa-inbox" />
              </EmptyStateIcon>
              <EmptyStateText>
                {searchQuery || statusFilter !== 'all'
                  ? 'No rows match your filter.'
                  : 'No rows in this import.'}
              </EmptyStateText>
            </EmptyState>
          ) : (
            filteredRows.map(row => {
              const validationErrors = row.validation_errors || [];
              const approvalErrors = row.approval_errors || [];
              const totalIssues = validationErrors.length + approvalErrors.length;

              return (
                <RowItem
                  key={row.id}
                  $active={selectedRowId === row.id}
                  onClick={() => this.selectRow(row.id)}
                >
                  <Checkbox
                    checked={!!selectedRowIds[row.id]}
                    onChange={e => this.toggleRowSelection(row.id, e.target.checked, e)}
                    onClick={e => e.stopPropagation()}
                    disabled={row.status === 'approved'}
                  />
                  <RowItemMain>
                    <RowItemTitle>
                      {getNested(row, 'normalized_payload.identificator', `Row ${row.row_index}`)}
                    </RowItemTitle>
                    <RowItemSub>
                      {getNested(row, 'normalized_payload.account_name', '')}
                      {getNested(row, 'normalized_payload.address_line', '')
                        ? ` · ${getNested(row, 'normalized_payload.address_line', '')}`
                        : ''}
                    </RowItemSub>
                  </RowItemMain>
                  <RowItemRight>
                    {totalIssues > 0 && (
                      <IssueIndicator $error={approvalErrors.length > 0}>
                        {totalIssues}
                      </IssueIndicator>
                    )}
                    <Badge $variant={getStatusVariant(row.status)}>
                      {getStatusLabel(row.status)}
                    </Badge>
                  </RowItemRight>
                </RowItem>
              );
            })
          )}
        </PaneBody>
      </>
    );
  };

  renderInspector = () => {
    const {
      rows,
      savingRow,
      brands,
      accounts,
      adminsLocations,
      states,
      cities,
      wfConfigs,
      roConfigs,
      softenerConfigs,
    } = this.props;
    const { selectedRowId, editorPayload } = this.state;
    const sortedBrands = sortFilterOptions(brands);
    const sortedAccounts = sortFilterOptions(accounts);
    const sortedLocationAdmins = [...(adminsLocations || [])].sort((a, b) =>
      `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(
        `${b.first_name || ''} ${b.last_name || ''}`,
      ),
    );

    const selectedRow = rows.find(row => row.id === selectedRowId);

    if (!selectedRow || !editorPayload) {
      return (
        <InspectorEmpty>
          <InspectorEmptyIcon>
            <i className="fas fa-hand-pointer" />
          </InspectorEmptyIcon>
          <InspectorEmptyText>Select a row to view and edit details</InspectorEmptyText>
        </InspectorEmpty>
      );
    }

    const validationErrors = selectedRow.validation_errors || [];
    const approvalErrors = selectedRow.approval_errors || [];
    const warnings = selectedRow.warnings || [];
    const hasIssues = validationErrors.length > 0 || approvalErrors.length > 0;
    const isApproved = selectedRow.status === 'approved';
    const hasWaterFilter = !!parseInt(
      getNested(editorPayload, 'waterFilter.water_filter_config_id', ''),
      10,
    );
    const hasReverseOsmosis = !!parseInt(
      getNested(editorPayload, 'reverseOsmosis.ro_filter_config_id', ''),
      10,
    );
    const hasSoftener = !!parseInt(
      getNested(editorPayload, 'waterSoftener.softener_configuration_id', ''),
      10,
    );
    const adminSelectionValue = deriveAdminSelectionValue(editorPayload);
    const isAddLaterSelection = adminSelectionValue === 'add_later';
    const useAdminAsPointOfContact = parseBooleanLike(
      editorPayload.isPointOfContact,
      !hasContactDetails(editorPayload),
    );
    const shouldShowPointOfContactFields = isAddLaterSelection || !useAdminAsPointOfContact;

    return (
      <>
        <PaneBody>
          {/* Issues Section */}
          {hasIssues && (
            <InspectorSection>
              <InspectorSectionTitle>Issues</InspectorSectionTitle>
              <IssuesList>
                {approvalErrors.map((err, i) => (
                  <IssueItem key={`ae-${i}`} $error>
                    <IssueIcon>
                      <i className="fas fa-times-circle" />
                    </IssueIcon>
                    {err}
                  </IssueItem>
                ))}
                {validationErrors.map((err, i) => (
                  <IssueItem key={`ve-${i}`} $error>
                    <IssueIcon>
                      <i className="fas fa-exclamation-circle" />
                    </IssueIcon>
                    {err}
                  </IssueItem>
                ))}
              </IssuesList>
            </InspectorSection>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <InspectorSection>
              <InspectorSectionTitle>Warnings</InspectorSectionTitle>
              <IssuesList>
                {warnings.map((w, i) => (
                  <IssueItem key={`w-${i}`}>
                    <IssueIcon>
                      <i className="fas fa-info-circle" />
                    </IssueIcon>
                    {w}
                  </IssueItem>
                ))}
              </IssuesList>
            </InspectorSection>
          )}

          {/* Device Configuration */}
          <InspectorSection>
            <InspectorSectionTitle>Device Configuration</InspectorSectionTitle>
            <DeviceConfigLayout>
              <DeviceConfigColumn>
                <DeviceConfigColumnTitle>Configurations</DeviceConfigColumnTitle>
                <FormGroup>
                  <Label>Water Filter</Label>
                  <SelectEl
                    value={getNested(editorPayload, 'waterFilter.water_filter_config_id', '')}
                    onChange={e =>
                      this.updateEditorField(
                        'waterFilter.water_filter_config_id',
                        e.target.value ? parseInt(e.target.value, 10) : '',
                      )
                    }
                    disabled={isApproved}
                  >
                    <option value="">Select config</option>
                    {wfConfigs.map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </SelectEl>
                </FormGroup>
                <FormGroup>
                  <Label>RO Config</Label>
                  <SelectEl
                    value={getNested(editorPayload, 'reverseOsmosis.ro_filter_config_id', '')}
                    onChange={e =>
                      this.updateEditorField(
                        'reverseOsmosis.ro_filter_config_id',
                        e.target.value ? parseInt(e.target.value, 10) : '',
                      )
                    }
                    disabled={isApproved}
                  >
                    <option value="">Select config</option>
                    {roConfigs.map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </SelectEl>
                </FormGroup>
                <FormGroup>
                  <Label>Softener</Label>
                  <SelectEl
                    value={getNested(editorPayload, 'waterSoftener.softener_configuration_id', '')}
                    onChange={e =>
                      this.updateEditorField(
                        'waterSoftener.softener_configuration_id',
                        e.target.value ? parseInt(e.target.value, 10) : '',
                      )
                    }
                    disabled={isApproved}
                  >
                    <option value="">Select config</option>
                    {softenerConfigs.map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </SelectEl>
                </FormGroup>
                <FormGroup>
                  <Label>Water Type</Label>
                  <SelectEl
                    value={getNested(editorPayload, 'waterFilter.chlorine_or_chloramine', '')}
                    onChange={e => {
                      this.updateEditorField('waterFilter.chlorine_or_chloramine', e.target.value);
                      this.updateEditorField(
                        'reverseOsmosis.chlorine_or_chloramine',
                        e.target.value,
                      );
                    }}
                    disabled={isApproved}
                  >
                    <option value="">Select type</option>
                    <option value="Chlorine">Chlorine</option>
                    <option value="Chloramine">Chloramine</option>
                  </SelectEl>
                </FormGroup>
              </DeviceConfigColumn>

              <DeviceConfigColumn>
                <DeviceConfigColumnTitle>Location IDs</DeviceConfigColumnTitle>
                {hasWaterFilter && (
                  <FormGroup>
                    <Label>Water Filter Location ID</Label>
                    <Input
                      value={getNested(editorPayload, 'waterFilter.location_identificator', '')}
                      onChange={e =>
                        this.updateEditorField('waterFilter.location_identificator', e.target.value)
                      }
                      disabled={isApproved}
                    />
                  </FormGroup>
                )}
                {hasReverseOsmosis && (
                  <FormGroup>
                    <Label>RO Location ID</Label>
                    <Input
                      value={getNested(editorPayload, 'reverseOsmosis.location_identificator', '')}
                      onChange={e =>
                        this.updateEditorField(
                          'reverseOsmosis.location_identificator',
                          e.target.value,
                        )
                      }
                      disabled={isApproved}
                    />
                  </FormGroup>
                )}
                {hasSoftener && (
                  <FormGroup>
                    <Label>Softener Location ID</Label>
                    <Input
                      value={getNested(editorPayload, 'waterSoftener.location_identificator', '')}
                      onChange={e =>
                        this.updateEditorField(
                          'waterSoftener.location_identificator',
                          e.target.value,
                        )
                      }
                      disabled={isApproved}
                    />
                  </FormGroup>
                )}
                {!hasWaterFilter && !hasReverseOsmosis && !hasSoftener && (
                  <DeviceConfigHint>
                    Select a configuration on the left to set its Location ID.
                  </DeviceConfigHint>
                )}
              </DeviceConfigColumn>
            </DeviceConfigLayout>
          </InspectorSection>

          {/* Location Details */}
          <InspectorSection>
            <InspectorSectionTitle>Location</InspectorSectionTitle>
            <FieldGrid>
              <FormGroup>
                <Label>Brand</Label>
                <SelectEl
                  value={editorPayload.brand_id || ''}
                  onChange={e => this.onBrandChange(e.target.value)}
                  disabled={isApproved}
                >
                  <option value="">Select brand</option>
                  {sortedBrands.map(brand => (
                    <option key={brand.value} value={brand.value}>
                      {brand.label}
                    </option>
                  ))}
                </SelectEl>
              </FormGroup>
              <FormGroup>
                <Label>Account</Label>
                <SelectEl
                  value={editorPayload.account_id || ''}
                  onChange={e => this.onAccountChange(e.target.value)}
                  disabled={isApproved}
                >
                  <option value="">Select account</option>
                  {sortedAccounts.map(a => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </SelectEl>
              </FormGroup>
              <FormGroup>
                <Label>Address Line 1</Label>
                <Input
                  value={editorPayload.address_line || ''}
                  onChange={e => this.updateEditorField('address_line', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
              <FormGroup>
                <Label>Address Line 2</Label>
                <Input
                  value={editorPayload.address_line_1 || ''}
                  onChange={e => this.updateEditorField('address_line_1', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
              <FormGroup>
                <Label>State</Label>
                <SelectEl
                  value={editorPayload.state_id || ''}
                  onChange={e => this.onStateChange(e.target.value)}
                  disabled={isApproved}
                >
                  <option value="">Select state</option>
                  {states.map(s => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </SelectEl>
              </FormGroup>
              <FormGroup>
                <Label>City</Label>
                <SelectEl
                  value={editorPayload.city_id || ''}
                  onChange={e =>
                    this.updateEditorField(
                      'city_id',
                      e.target.value ? parseInt(e.target.value, 10) : '',
                    )
                  }
                  disabled={isApproved}
                >
                  <option value="">Select city</option>
                  {cities.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </SelectEl>
              </FormGroup>
              <FormGroup>
                <Label>ZIP</Label>
                <Input
                  value={editorPayload.zip_code || ''}
                  onChange={e => {
                    const val = e.target.value;
                    this.updateEditorField('zip_code', val);
                    const zip = getZipForLookup(val);
                    if (zip?.length === 5 && (!editorPayload.state_id || !editorPayload.city_id)) {
                      this.resolveFromZipCode(val);
                    }
                  }}
                  disabled={isApproved}
                />
              </FormGroup>
              <FormGroup>
                <Label>Store #</Label>
                <Input
                  value={editorPayload.store_number || ''}
                  onChange={e => this.updateEditorField('store_number', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
            </FieldGrid>
          </InspectorSection>

          {/* Location Admin */}
          <InspectorSection>
            <InspectorSectionTitle>Location Admin</InspectorSectionTitle>
            <FieldGrid>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>Location Admin Setup</Label>
                <SelectEl
                  value={adminSelectionValue}
                  onChange={e => this.applyAdminSelection(e.target.value)}
                  disabled={isApproved || !editorPayload.account_id}
                >
                  <option value="new_user">New User</option>
                  <option value="add_later">Add later</option>
                  {sortedLocationAdmins.map(admin => {
                    const fullName = `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
                    return (
                      <option key={admin.id} value={admin.id}>
                        {admin.email ? `${fullName} (${admin.email})` : fullName}
                      </option>
                    );
                  })}
                </SelectEl>
              </FormGroup>

              {adminSelectionValue === 'new_user' && (
                <>
                  <FormGroup>
                    <Label>First Name</Label>
                    <Input
                      value={editorPayload.admin_first_name || ''}
                      onChange={e => this.updateEditorField('admin_first_name', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Last Name</Label>
                    <Input
                      value={editorPayload.admin_last_name || ''}
                      onChange={e => this.updateEditorField('admin_last_name', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      value={editorPayload.admin_email || ''}
                      onChange={e => this.updateEditorField('admin_email', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone Number</Label>
                    <Input
                      value={editorPayload.admin_phone_number || ''}
                      onChange={e => this.updateEditorField('admin_phone_number', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup style={{ gridColumn: '1 / -1' }}>
                    <Label>Position</Label>
                    <Input
                      value={editorPayload.admin_position || ''}
                      onChange={e => this.updateEditorField('admin_position', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                </>
              )}
            </FieldGrid>
          </InspectorSection>

          {/* Point Of Contact */}
          <InspectorSection>
            <InspectorSectionTitle>Point of Contact</InspectorSectionTitle>
            <FieldGrid>
              {isAddLaterSelection ? (
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <div
                    style={{
                      fontSize: '12px',
                      color: tokens.accent,
                      background: tokens.infoBg,
                      border: `1px solid ${tokens.borderSubtle}`,
                      borderRadius: tokens.radius,
                      padding: '8px 10px',
                    }}
                  >
                    If you cannot create a location admin at this time, a point of contact must be
                    entered.
                  </div>
                </FormGroup>
              ) : (
                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label style={{ textTransform: 'none', letterSpacing: 0, fontSize: '12px' }}>
                    Use location admin as point of contact
                  </Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Checkbox
                      checked={useAdminAsPointOfContact}
                      onChange={e => this.updateEditorField('isPointOfContact', e.target.checked)}
                      disabled={isApproved}
                    />
                    <span style={{ fontSize: '12px', color: tokens.textMuted }}>
                      Toggle off to enter a manual point of contact.
                    </span>
                  </div>
                </FormGroup>
              )}

              {shouldShowPointOfContactFields && (
                <>
                  <FormGroup>
                    <Label>First Name</Label>
                    <Input
                      value={editorPayload.contact_first_name || ''}
                      onChange={e => this.updateEditorField('contact_first_name', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Last Name</Label>
                    <Input
                      value={editorPayload.contact_last_name || ''}
                      onChange={e => this.updateEditorField('contact_last_name', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      value={editorPayload.contact_email || ''}
                      onChange={e => this.updateEditorField('contact_email', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone Number</Label>
                    <Input
                      value={editorPayload.contact_phone_number || ''}
                      onChange={e => this.updateEditorField('contact_phone_number', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                  <FormGroup style={{ gridColumn: '1 / -1' }}>
                    <Label>Position</Label>
                    <Input
                      value={editorPayload.contact_position || 'N/A'}
                      onChange={e => this.updateEditorField('contact_position', e.target.value)}
                      disabled={isApproved}
                    />
                  </FormGroup>
                </>
              )}
            </FieldGrid>
          </InspectorSection>

          {/* Installer */}
          <InspectorSection>
            <InspectorSectionTitle>Installer</InspectorSectionTitle>
            <FieldGrid>
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  value={editorPayload.installer_first_name || ''}
                  onChange={e => this.updateEditorField('installer_first_name', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  value={editorPayload.installer_last_name || ''}
                  onChange={e => this.updateEditorField('installer_last_name', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  value={editorPayload.installer_email || ''}
                  onChange={e => this.updateEditorField('installer_email', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
              <FormGroup>
                <Label>Phone</Label>
                <Input
                  value={editorPayload.installer_phone_number || ''}
                  onChange={e => this.updateEditorField('installer_phone_number', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>Company</Label>
                <Input
                  value={editorPayload.installer_company_name || ''}
                  onChange={e => this.updateEditorField('installer_company_name', e.target.value)}
                  disabled={isApproved}
                />
              </FormGroup>
            </FieldGrid>
          </InspectorSection>
        </PaneBody>

        {!isApproved && (
          <InspectorActions>
            <BtnPrimary onClick={this.saveRow} disabled={savingRow} style={{ flex: 1 }}>
              {savingRow ? 'Saving...' : 'Save Changes'}
            </BtnPrimary>
          </InspectorActions>
        )}
      </>
    );
  };

  render() {
    const { currentImport, approving, lastApproveResult } = this.props;
    const { selectedImportId, confirmModal, showResultBanner } = this.state;

    const selectedCount = this.getSelectedCount();
    const hasApproveResult =
      showResultBanner &&
      lastApproveResult &&
      (lastApproveResult.approved || lastApproveResult.failed);
    const approvedCount = hasApproveResult ? lastApproveResult.approved?.length || 0 : 0;
    const failedCount = hasApproveResult ? lastApproveResult.failed?.length || 0 : 0;

    return (
      <PageShell>
        {/* Top Bar */}
        <TopBar>
          <TopBarLeft>
            <PageTitle>Location Import</PageTitle>
            {currentImport && (
              <>
                <StatBadge>
                  <i className="fas fa-check-circle" style={{ color: tokens.success }} />
                  {currentImport.ready_rows || 0} ready
                </StatBadge>
                <StatBadge>
                  <i className="fas fa-exclamation-circle" style={{ color: tokens.warning }} />
                  {currentImport.needs_data_rows || 0} need data
                </StatBadge>
                <StatBadge>
                  <i className="fas fa-check-double" style={{ color: tokens.accent }} />
                  {currentImport.approved_rows || 0} approved
                </StatBadge>
              </>
            )}
          </TopBarLeft>
          <TopBarRight>
            <BtnSecondary onClick={this.selectAllVisible} disabled={!selectedImportId}>
              Select Ready
            </BtnSecondary>
            <BtnSecondary
              onClick={this.approveSelected}
              disabled={selectedCount === 0 || approving}
            >
              Approve Selected ({selectedCount})
            </BtnSecondary>
            <BtnPrimary
              onClick={this.approveAllReady}
              disabled={!currentImport?.ready_rows || approving}
            >
              {approving ? 'Approving...' : 'Approve All Ready'}
            </BtnPrimary>
          </TopBarRight>
        </TopBar>

        {/* Result Banner */}
        {hasApproveResult && (
          <ResultBanner $hasErrors={failedCount > 0}>
            <span>
              <strong>{approvedCount}</strong> approved, <strong>{failedCount}</strong> failed
            </span>
            <ResultBannerClose onClick={this.closeResultBanner}>&times;</ResultBannerClose>
          </ResultBanner>
        )}

        {/* Split Pane */}
        <SplitPane>
          {/* Left Pane: Rows */}
          <Pane>{this.renderRowList()}</Pane>

          {/* Right Pane: Inspector */}
          <Pane $noBorder>
            <PaneHeader>
              <PaneTitle>Row Details</PaneTitle>
              {this.hasUnsavedChanges() && <Badge $variant="warning">Unsaved</Badge>}
            </PaneHeader>
            {this.renderInspector()}
          </Pane>
        </SplitPane>

        {/* Confirm Modal */}
        {confirmModal && (
          <ModalOverlay onClick={this.closeConfirmModal}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{confirmModal.title}</ModalTitle>
              </ModalHeader>
              <ModalBody>{confirmModal.message}</ModalBody>
              <ModalFooter>
                <BtnSecondary onClick={this.closeConfirmModal}>Cancel</BtnSecondary>
                <BtnPrimary onClick={confirmModal.onConfirm}>
                  {confirmModal.confirmLabel}
                </BtnPrimary>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageShell>
    );
  }
}

LocationImportApprovalContainer.propTypes = {
  imports: PropTypes.array,
  currentImport: PropTypes.object,
  rows: PropTypes.array,
  rowsLoading: PropTypes.bool,
  uploading: PropTypes.bool,
  savingRow: PropTypes.bool,
  approving: PropTypes.bool,
  token: PropTypes.string,
  brands: PropTypes.array,
  accounts: PropTypes.array,
  adminsLocations: PropTypes.array,
  states: PropTypes.array,
  cities: PropTypes.array,
  wfConfigs: PropTypes.array,
  roConfigs: PropTypes.array,
  softenerConfigs: PropTypes.array,
  fetchImports: PropTypes.func,
  fetchImport: PropTypes.func,
  saveImportRow: PropTypes.func,
  approveImportRows: PropTypes.func,
  deleteLocationImport: PropTypes.func,
  deleting: PropTypes.bool,
  fetchBrands: PropTypes.func,
  fetchAccounts: PropTypes.func,
  fetchAdminsLocations: PropTypes.func,
  fetchStates: PropTypes.func,
  fetchCities: PropTypes.func,
  fetchWfConfigs: PropTypes.func,
  fetchRoConfigs: PropTypes.func,
  fetchSoftenerConfigs: PropTypes.func,
  receiveUploadedImport: PropTypes.func,
  clearApproveResult: PropTypes.func,
  lastApproveResult: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    imports: state.locationImports.list || [],
    currentImport: state.locationImports.currentImport,
    rows: state.locationImports.rows || [],
    rowsLoading: state.locationImports.rowsLoading,
    uploading: state.locationImports.uploading,
    savingRow: state.locationImports.savingRow,
    approving: state.locationImports.approving,
    deleting: state.locationImports.deleting,
    token: state.auth.token,
    brands: state.brands.listForFilter || [],
    accounts: state.accounts.listForFilter || [],
    adminsLocations: state.brands.adminsLocations || [],
    states: state.utils.states || [],
    cities: state.utils.cities || [],
    wfConfigs: state.libraries.waterFilterConfigurationsForFilter || [],
    roConfigs: state.libraries.reverseOsmosisConfigurationsForFilter || [],
    softenerConfigs: state.libraries.softenerConfigurationsForFilter || [],
    lastApproveResult: state.locationImports.lastApproveResult,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchImports: params => dispatch(getLocationImports(params)),
    fetchImport: (id, params) => dispatch(getLocationImport(id, params)),
    saveImportRow: (importId, rowId, row) =>
      dispatch(updateLocationImportRow(importId, rowId, row)),
    approveImportRows: (importId, payload) => dispatch(approveLocationImport(importId, payload)),
    deleteLocationImport: id => dispatch(deleteLocationImport(id)),
    fetchBrands: params => dispatch(getBrandsForFilter(params)),
    fetchAccounts: params => dispatch(getAccountsForFilter(params)),
    fetchAdminsLocations: accountId => dispatch(getAdminsLocations(accountId)),
    fetchStates: () => dispatch(getStates()),
    fetchCities: stateId => dispatch(getCitiesByState(stateId)),
    fetchWfConfigs: () => dispatch(getWaterFilterConfigurationsForFilter()),
    fetchRoConfigs: () => dispatch(getReverseOsmosisConfigurationsForFilter()),
    fetchSoftenerConfigs: () => dispatch(getSoftenerConfigurationsForFilter()),
    receiveUploadedImport: payload =>
      dispatch({
        type: ActionTypes.UPLOAD_LOCATION_IMPORT_SUCCESS,
        payload,
      }),
    clearApproveResult: () =>
      dispatch({
        type: ActionTypes.CLEAR_LOCATION_IMPORT_APPROVE_RESULT,
      }),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationImportApprovalContainer);
